import { LayoutGrid, Search } from 'lucide-react';

import { useTheme } from '../../hooks/useTheme';

import type { NavRailItem } from './config';
import { NAV_THEMES } from './nav-tokens';
import NavBottomTab from './NavBottomTab';

interface NebariBottomBarProps {
  tabs: NavRailItem[];
  drawerOpen: boolean;
  /** Omit to leave More off the bar; the hamburger still opens the drawer. */
  onOpenDrawer?: () => void;
  /** Omit for a mode without search; the destinations then sit side by side. */
  onOpenSearch?: () => void;
}

/** The mobile bottom tab bar: the common destinations around a raised search
 *  button, then More, which opens the same drawer as the hamburger. Search and
 *  More each show only when the mode offers them.
 *  Hidden from 768px up. */
export default function NebariBottomBar({
  tabs,
  drawerOpen,
  onOpenDrawer,
  onOpenSearch,
}: NebariBottomBarProps) {
  const { theme } = useTheme();
  const t = NAV_THEMES[theme];
  const accent = t.accent;
  // With search, the bar always shows: a viewer whose every tab is gated away
  // still needs Search and More. Without it, an empty bar would be just More.
  if (!onOpenSearch && tabs.length === 0) return null;
  // Search takes the middle slot, so the destinations split around it. The
  // split is computed rather than fixed at two: a permission-gated tab can
  // drop out, and slicing at a constant would push the raised circle off
  // centre — the one thing this bar is arranged around.
  const split = onOpenSearch ? Math.ceil(tabs.length / 2) : tabs.length;
  const left = tabs.slice(0, split);
  const right = tabs.slice(split);

  return (
    <nav
      aria-label="Primary"
      // `nb-bottom-bar` carries the height and the home-indicator inset —
      // see index.css. `relative` + z-index so the raised Search circle, which
      // overhangs the top edge, paints over the scrolling content rather than
      // being cut off at the bar's border.
      className="nb-bottom-bar relative z-20 flex shrink-0 items-stretch px-1 navtab:hidden"
      style={{ background: t.bg, borderTop: `1px solid ${t.border}` }}
    >
      {left.map((tab) => (
        <NavBottomTab key={tab.id} item={tab} accent={accent} idle={t.subtle} />
      ))}
      {onOpenSearch && (
        <button
          type="button"
          onClick={onOpenSearch}
          aria-label="Search"
          className="flex flex-1 cursor-pointer flex-col items-center justify-center gap-0.5 border-0 bg-transparent"
          style={{ color: t.ink }}
        >
          <span
            className="-mt-3 flex h-11 w-11 items-center justify-center rounded-full"
            style={{ background: accent, color: '#fff', boxShadow: '0 2px 10px rgba(0,0,0,.18)' }}
          >
            <Search size={20} aria-hidden />
          </span>
          <span className="text-[9.5px] font-bold tracking-wide">Search</span>
        </button>
      )}
      {right.map((tab) => (
        <NavBottomTab key={tab.id} item={tab} accent={accent} idle={t.subtle} />
      ))}
      {onOpenDrawer && (
        <button
          type="button"
          onClick={onOpenDrawer}
          aria-label="More navigation"
          aria-expanded={drawerOpen}
          className="flex flex-1 cursor-pointer flex-col items-center justify-center gap-0.5 border-0 bg-transparent"
          style={{ color: drawerOpen ? accent : t.subtle }}
        >
          <LayoutGrid size={20} aria-hidden />
          <span className="text-[9.5px] font-bold tracking-wide">More</span>
        </button>
      )}
    </nav>
  );
}
